"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type RefObject, type ReactNode } from "react";
import { cn } from "@/lib/utils";

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    const element = ref.current;
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function getDelayClass(delay: number) {
  if (delay <= 0) return "delay-0";
  if (delay < 0.1) return "delay-75";
  if (delay < 0.16) return "delay-150";
  if (delay < 0.22) return "delay-200";
  return "delay-300";
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

type GrantFaq = {
  q: string;
  a: string;
};

type GrantService = {
  n: string;
  title: string;
  body: string;
};

type GrantStep = {
  n: string;
  title: string;
  body: string;
};

const SERVICES: GrantService[] = [
  {
    n: "01",
    title: "Scheme Identification",
    body: "We analyse your business profile, sector, turnover and objectives to identify the most relevant grant schemes from 100+ available programs.",
  },
  {
    n: "02",
    title: "Eligibility Assessment",
    body: "A thorough eligibility review across multiple grant programs ensures your application aligns with all criteria before you invest time and resources.",
  },
  {
    n: "03",
    title: "Strategic Application Planning",
    body: "We develop a clear application strategy — highlighting your business strengths, impact potential and alignment with the scheme's objectives.",
  },
  {
    n: "04",
    title: "Proposal Development",
    body: "We prepare compelling, well-structured grant proposals including project plans, financial projections, impact assessments and supporting documentation.",
  },
  {
    n: "05",
    title: "Documentation Support",
    body: "We assist in gathering, organising and formatting all required documents — financials, KYC, registration certificates, business plans and more.",
  },
  {
    n: "06",
    title: "Application Submission",
    body: "We manage the complete end-to-end submission process, ensuring accuracy, completeness and timely filing across all official portals.",
  },
  {
    n: "07",
    title: "Interview & Presentation Support",
    body: "We prepare your team for evaluation discussions with funding authorities — mock sessions, key message coaching and material preparation.",
  },
  {
    n: "08",
    title: "Post-Approval Compliance",
    body: "We support ongoing compliance with grant conditions — utilisation certificates, progress reports, audit requirements and documentation.",
  },
];

const SCHEMES = [
  {
    scheme: "MSME Technology Upgradation (CLCSS)",
    eligible: "MSMEs in manufacturing",
    benefit: "15% capital subsidy on technology upgradation",
  },
  {
    scheme: "Startup India Seed Fund",
    eligible: "DPIIT-recognised startups",
    benefit: "Up to ₹20 lakh for validation and prototyping",
  },
  {
    scheme: "Stand-Up India",
    eligible: "SC/ST & women entrepreneurs",
    benefit: "Loans ₹10 lakh – ₹1 crore + support",
  },
  {
    scheme: "PM Vishwakarma Yojana",
    eligible: "Traditional artisans & craftspeople",
    benefit: "Training, toolkit, credit up to ₹3 lakh",
  },
  {
    scheme: "Production Linked Incentive (PLI)",
    eligible: "Sector-specific manufacturers",
    benefit: "5–20% incentive on incremental production",
  },
  {
    scheme: "Digital India MSME Scheme",
    eligible: "Digitising MSMEs",
    benefit: "Subsidised technology adoption support",
  },
  {
    scheme: "NIDHI Programme (DST)",
    eligible: "Deep-tech startups",
    benefit: "Funding up to ₹50 lakh for incubation",
  },
  {
    scheme: "State Industrial Incentives",
    eligible: "Location-specific businesses",
    benefit: "Varies by state — capital subsidy, power, land",
  },
];

const STEPS: GrantStep[] = [
  {
    n: "01",
    title: "Business Profile Analysis",
    body: "We review your registration status, sector, turnover and growth objectives to build your eligibility profile.",
  },
  {
    n: "02",
    title: "Grant Landscape Mapping",
    body: "We identify all applicable schemes at central, state and sector level from 100+ programs.",
  },
  {
    n: "03",
    title: "Eligibility Verification",
    body: "We confirm your eligibility against all scheme criteria before you invest time and resources.",
  },
  {
    n: "04",
    title: "Application Strategy",
    body: "We define the strongest narrative and approach for your application based on scheme objectives.",
  },
  {
    n: "05",
    title: "Document Preparation",
    body: "We compile and organise every required document to specification — nothing missed.",
  },
  {
    n: "06",
    title: "Proposal Writing",
    body: "We write a compelling, structured grant proposal aligned with the scheme's stated objectives.",
  },
  {
    n: "07",
    title: "Submission & Follow-Up",
    body: "We submit through official channels, track application status and handle queries from authorities.",
  },
  {
    n: "08",
    title: "Post-Award Support",
    body: "We ensure full compliance with grant conditions — reporting, audits and utilisation certificates.",
  },
];

const FAQS: GrantFaq[] = [
  {
    q: "How long does the grant process take?",
    a: "Typically 2–6 months from application to approval, depending on the scheme. Some state schemes have faster turnaround of 4–8 weeks.",
  },
  {
    q: "Do I need to repay a government grant?",
    a: "No. Government grants are non-repayable. You are only required to meet grant conditions such as reporting and utilisation requirements.",
  },
  {
    q: "Is my business eligible?",
    a: "Eligibility depends on your business type, sector, turnover, location and the specific scheme. We assess this in the first consultation — free of charge.",
  },
  {
    q: "What documents do I need?",
    a: "Typically: GST registration, MSME certificate, audited financials, KYC, bank statements and a business plan. We guide you through everything.",
  },
  {
    q: "Can startups apply for grants?",
    a: "Yes. DPIIT-recognised startups have access to several exclusive schemes including the Startup India Seed Fund and the NIDHI programme.",
  },
  {
    q: "What is your success rate?",
    a: "We maintain a 95%+ application success rate for clients who are genuinely eligible and complete the process with our full support.",
  },
];

const BENEFITS = [
  "Non-repayable funding — no debt, no equity dilution",
  "Sector-specific schemes for manufacturing, tech, agriculture, healthcare",
  "Subsidies for capital investment, employment, R&D and exports",
  "Schemes for MSMEs, startups, women entrepreneurs, SC/ST founders",
  "Access to central, state and international grant programs",
  "Expert guidance from scheme identification to post-award compliance",
];

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-[44px] h-[3px] bg-[#1565C0] mb-6", className)} />;
}

function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <span
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center transition-colors duration-200",
            open ? "bg-[#1565C0] text-white" : "bg-[#F8FAFC] text-[#1565C0]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            {open ? (
              <path d="M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            ) : (
              <>
                <path d="M6 2v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </>
            )}
          </svg>
        </span>
      </button>

      {open && <p className="pb-5 text-[14px] leading-[1.85] text-[#64748B]">{a}</p>}
    </div>
  );
}

function ServiceCard({ s, i }: { s: GrantService; i: number }) {
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "flex gap-[18px] rounded-[10px] border border-[#E2E8F0] bg-white p-[28px_26px] transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] flex-shrink-0">
        <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-white">{s.n}</span>
      </div>
      <div>
        <div className="text-[14px] font-semibold text-[#0F172A] mb-2">{s.title}</div>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

function Step({ s, i, total }: { s: GrantStep; i: number; total: number }) {
  const [ref, visible] = useVisible();
  const delayClass = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"][Math.min(i, 4)];

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 pb-8 transition-all duration-500 ease-out",
        delayClass,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      {i < total - 1 && (
        <span className="absolute left-[17px] top-[36px] bottom-0 w-px bg-[#E2E8F0]" />
      )}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] flex-shrink-0">
        <span className="text-[11px] font-semibold text-white">{s.n}</span>
      </div>
      <div className="pt-[6px]">
        <div className="text-[14px] font-semibold text-[#0F172A] mb-1.5">{s.title}</div>
        <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{s.body}</p>
      </div>
    </div>
  );
}

export default function GovernmentGrantsPage() {
  return (
    <main className="bg-white text-[#0F172A]">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pr-[30px] pl-4 sm:pl-8 lg:pl-[80px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#64748B] mb-6">
            <Link href="/services" className="text-[#1565C0] font-medium hover:underline">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/funding" className="text-[#1565C0] font-medium hover:underline">
              Funding
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span>Government Grants</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {[
              "MSME",
              "Startup India",
              "DPIIT",
              "Sector-Specific Schemes",
            ].map((tag) => (
              <span
                key={tag}
                className="text-[11px] font-semibold text-[#1565C0] bg-[#EFF6FF] py-1 px-2.5 tracking-[0.04em] rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
            Funding Assistance — Grants
          </span>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,44px)] font-extrabold leading-[1.1] mb-6 max-w-[38rem]">
            Unlock Non-Repayable Government Funding for Your Business
          </h1>
          <p className="text-[16px] leading-[1.85] text-[#64748B] mb-9 ">
            Government grants provide capital without debt, equity dilution, or interest obligations. But navigating eligibility and applications is complex — Farsight handles it all.
          </p>
          <div className="flex flex-wrap gap-3">
            <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-7 py-3 text-[13px] font-bold tracking-[0.06em]">
              BOOK FREE CONSULTATION
            </button>
            <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-7 py-3 text-[13px] font-semibold tracking-[0.06em]">
              DOWNLOAD GRANT GUIDE
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[320px] lg:min-h-[400px]">
          <Image
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=85"
            alt="Government grants"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { v: "100+", l: "Grant Schemes Covered" },
          { v: "95%+", l: "Application Success Rate" },
          { v: "₹50Cr+", l: "Funds Facilitated" },
          { v: "Free", l: "Initial Consultation" },
        ].map((item, idx) => (
          <div
            key={idx}
            className={cn(
              "py-5 text-center",
              idx < 3 ? "lg:border-r border-white/15" : ""
            )}
          >
            <div className="text-[24px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <Reveal from="left">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
              Why Government Grants
            </span>
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-5">
              Free Capital That Fuels Real Growth
            </h2>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-4">
              Government grants are among the most powerful — and underutilised — funding tools available to Indian businesses. Unlike loans, grants do not require repayment. Unlike equity, they do not dilute ownership.
            </p>
            <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
              Farsight Solutions provides professional grant consultancy services — helping businesses identify the right schemes, assess eligibility, prepare compelling applications and maintain post-award compliance.
            </p>
            <div className="space-y-4">
              {BENEFITS.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="mt-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0]">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span className="text-[14px] leading-[1.75] text-[#334155]">{benefit}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[240px] sm:h-[340px] lg:h-[420px] overflow-hidden rounded-[22px]">
                <Image
                  src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=700&q=80"
                  alt="Business funding"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 -left-6 w-[310px] rounded-[22px] border border-[#E2E8F0] bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.10)]">
                <div className="text-[32px] font-black text-[#1565C0] leading-none">₹50Cr+</div>
                <div className="text-[11px] uppercase tracking-[0.05em] text-[#64748B] font-medium mt-1">
                  Funds Facilitated
                </div>
                <div className="text-[30px] font-black text-[#E65100] leading-none mt-4">95%+</div>
                <div className="text-[11px] uppercase tracking-[0.05em] text-[#64748B] font-medium mt-1">
                  Success Rate
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
                Our Grant Consultancy Services
              </span>
              <Divider />
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18]">
                  End-to-End Grant Management
                </h2>
                <p className="text-[14px] leading-[1.85] text-[#64748B] max-w-[380px]">
                  From identifying the right scheme to post-award compliance — we manage every step.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-2 md:grid-cols-2">
            {SERVICES.map((service, index) => (
              <ServiceCard key={index} s={service} i={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
                Grant Schemes We Cover
              </span>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18] mb-4">
                8 Major Government Schemes
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] ">
                We cover 100+ central, state and sector-specific schemes. Below are the most impactful programs for Indian MSMEs and startups.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 text-[14px]">
                <thead className="bg-[#1565C0]">
                  <tr>
                    {['Scheme', 'Eligible For', 'Key Benefit'].map((heading) => (
                      <th
                        key={heading}
                        className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-white whitespace-nowrap"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SCHEMES.map((scheme, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}
                    >
                      <td className="px-5 py-4 font-semibold text-[#0F172A] leading-[1.5]">
                        {scheme.scheme}
                      </td>
                      <td className="px-5 py-4 text-[#334155] leading-[1.5]">
                        {scheme.eligible}
                      </td>
                      <td className="px-5 py-4 text-[#64748B] leading-[1.5]">
                        {scheme.benefit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0] mb-3.5">
                Our Process
              </span>
              <Divider />
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18]">
                  8-Step Grant Application Process
                </h2>
                <p className="text-[14px] leading-[1.85] text-[#64748B] max-w-[360px]">
                  A structured, proven methodology that maximises your chances of approval.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-0 lg:grid-cols-2">
            <div>
              {STEPS.slice(0, 4).map((step, index) => (
                <Step key={step.n} s={step} i={index} total={4} />
              ))}
            </div>
            <div>
              {STEPS.slice(4).map((step, index) => (
                <Step key={step.n} s={step} i={index + 4} total={4} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-start">
          <Reveal from="left">
            <div className="space-y-5">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">
                Frequently Asked Questions
              </span>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18]">Common Questions About Government Grants</h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] max-w-[32rem]">
                Everything you need to know before starting your grant application journey with Farsight Solutions.
              </p>
              <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-[22px]">
                <Image
                  src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=700&q=80"
                  alt="FAQ"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-[#E2E8F0] pt-8">
              {FAQS.map((faq, index) => (
                <FaqItem key={index} q={faq.q} a={faq.a} i={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-2 items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[360px] overflow-hidden rounded-[22px]">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
                alt="Book consultation"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="space-y-5">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1565C0]">
                Ready to Access Government Funding?
              </span>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,38px)] font-bold leading-[1.18]">
                Book a Free Consultation
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B]">
                We will identify the grant schemes your business qualifies for — at no cost, no commitment.
              </p>
              <p className="text-[15px] leading-[1.85] text-[#64748B]">
                Our experts assess your eligibility across 100+ schemes in the first session and give you a clear roadmap to funding.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center justify-center rounded-sm bg-[#1565C0] text-white px-8 py-3 text-[13px] font-bold tracking-[0.06em]">
                  BOOK FREE CONSULTATION
                </button>
                <button className="inline-flex items-center justify-center rounded-sm border border-[#E2E8F0] bg-transparent text-[#0F172A] px-8 py-3 text-[13px] font-semibold tracking-[0.06em]">
                  DOWNLOAD GRANT GUIDE
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
